import type { Asset } from '@schibsted-svp/svp-api-types';
import {
    getPulseTracker,
    type Tracker,
    type IEngagementVideo,
    type IEngagementAudio,
    type IEngagementVideoAd,
} from '@schibsted/pulse-sdk';
import logger from 'utils/logger';
import { PluginModel } from 'player/plugins/model';
import { AdData, AdEvents } from './tpaas/ad-events';
import PlaybackEvents from './tpaas/playback-events';
import { normalizeAspectRatio } from '../playback/provider';
import { capitalizeFirstLetter } from '../../utils/string';
import { normalizeString, normalizeContentSource } from './tpaas/utils';
import { resolvePlayEntry } from './tpaas/play-entry';
import { type FoldPosition, getFoldPosition } from './tpaas/fold-position';

type VideoAdEngagementType = Exclude<
    Exclude<IEngagementVideoAd['engagement'], undefined>['engagementType'],
    undefined
>;
type VideoEngagementType = Exclude<
    Exclude<IEngagementVideo['engagement'], undefined>['engagementType'],
    undefined
>;
type AudioEngagementType = Exclude<
    Exclude<IEngagementAudio['engagement'], undefined>['engagementType'],
    undefined
>;

type AudioDisplayMode = Exclude<
    IEngagementAudio['interaction'],
    undefined
>['displayMode'];

type VideoInteraction = Exclude<IEngagementVideo['interaction'], undefined>;
type VideoPlayType = Exclude<
    IEngagementVideo['interaction'],
    undefined
>['playType'];
type VideoPlayTypeIntent = Exclude<
    IEngagementVideo['object'],
    undefined
>['playTypeIntent'];

type AppState = VideoInteraction['appState'];
type VideoDisplayMode = VideoInteraction['displayMode'];
type PlaybackDevice = VideoInteraction['playbackDevice'];

type PlayTrigger = 'Autoplay' | 'Click' | 'Scroll' | 'None';

/**
 * Event types that mark the (re)start of an asset's playback flow. The
 * play trigger is computed on these and cached for the asset's later
 * engagement events (Watch/Pause/Stop/Seek), which may lack playMethod/
 * playbackSource. Not typed as VideoEngagementType | AudioEngagementType
 * because the TPaaS schema's engagementType omits "Replay", even though
 * playback-events.ts triggers it.
 */
const ASSET_START_ENGAGEMENT_TYPES = new Set<string>([
    'Start',
    'Resume',
    'Replay',
    'Load',
]);

function isVideoAsset(asset: {
    assetType: 'video' | 'audio';
}): asset is { assetType: 'video' } {
    return asset.assetType === 'video';
}

function isAudioAsset(asset: {
    assetType: 'video' | 'audio';
}): asset is { assetType: 'audio' } {
    return asset.assetType === 'audio';
}

type TpaasPluginOptions = {
    provider: string;
    sdkConfig: never;
    embed?: boolean;
    previousAssetId?: number;
    tts?: {
        enabled: boolean;
    };
    engagementIntervals?: {
        vod?: number;
        live?: number;
    };
};

type BrandedAsset = Asset & {
    vendor: string;
    _embedded: {
        tags: { id: string; tag: string }[];
    };
};

/**
 * Integration with TPaaS Pulse Tracker
 */
export class TpaasPlugin extends PluginModel<TpaasPluginOptions> {
    setupErrorEventEmitted: boolean = false;
    embed: boolean;
    isMockAsset: boolean;
    provider: string;
    previousAssetId: number | undefined;
    tracker: Tracker | null;
    playerAsset: BrandedAsset = null as unknown as BrandedAsset;
    sdkConfig: unknown; // TODO: needed
    intervals: {
        vod?: number;
        live?: number;
    };
    tts: { enabled: boolean };

    adEvents: AdEvents = null!;
    playbackEvents: PlaybackEvents = null!;
    casting: boolean = false;

    prevPositionMs = 0;
    playEntry: string | undefined;
    // computed on asset-start events (Start/Resume/Replay/Load) and reused
    // for the asset's later engagement events, see onPlaybackEvent
    playTrigger: PlayTrigger | null = null;

    constructor(options: TpaasPluginOptions, isMockAsset: boolean) {
        super(options);

        this.onPlaybackEvent = this.onPlaybackEvent.bind(this);
        this.onAdEvent = this.onAdEvent.bind(this);
        this.onViewable = this.onViewable.bind(this);
        this.onNewAsset = this.onNewAsset.bind(this);
        this.onCast = this.onCast.bind(this);

        this.provider = options.provider;
        this.sdkConfig = options.sdkConfig;
        this.intervals = {
            ...(options?.engagementIntervals?.live && {
                live: options.engagementIntervals.live,
            }),
            ...(options?.engagementIntervals?.vod && {
                vod: options?.engagementIntervals?.vod,
            }),
        };
        this.embed = options.embed === true;
        this.previousAssetId = options.previousAssetId;
        this.tracker = null;
        this.isMockAsset = isMockAsset;
        this.tts = options.tts || { enabled: false };
    }

    getName() {
        return 'Tpaas';
    }

    get jw() {
        return this.player.getJWPlayer();
    }

    getPlayTypeIntent(): VideoPlayTypeIntent {
        const autoplay = this.player.config.get('autoplay');
        if (autoplay) {
            return 'Automatic';
        }
        return 'Manual';
    }

    getPlayType(playTrigger: PlayTrigger | null): VideoPlayType {
        // actual behavior: a Click is a user-initiated (Manual) start;
        // Autoplay/Scroll/None are non-interactive (Automatic) starts.
        // null trigger (e.g. an ad before any asset-start) defaults to Automatic.
        return playTrigger === 'Click' ? 'Manual' : 'Automatic';
    }

    getPlayTrigger(
        playMethod?: jwplayer.PlayReason | string | null,
        playbackSource?: string,
    ): PlayTrigger {
        if (playbackSource?.startsWith('manualNext')) {
            return 'Click';
        }
        if (playbackSource?.startsWith('autoplayNext')) {
            return 'None';
        }
        if (playMethod === 'interaction') {
            return 'Click';
        }
        if (
            playMethod === 'viewable' ||
            this.player.config.get('autoplay') === 'viewable'
        ) {
            return 'Scroll';
        }
        if (playMethod === 'playlist' || playMethod === 'related-auto') {
            return 'None';
        }
        if (playMethod === 'autostart') {
            return 'Autoplay';
        }
        if (playMethod === 'external') {
            // residual "external" (not caught by the rules above) comes
            // from user-driven play buttons/resumes
            return 'Click';
        }

        // playMethod is null/unknown (e.g. a later tick for the same
        // asset, see onPlaybackEvent's caching), fall back to config
        const autoplay = this.player.config.get('autoplay');
        if (autoplay === true) {
            return 'Autoplay';
        }
        if (autoplay === 'viewable') {
            return 'Scroll';
        }
        return 'Click';
    }

    getDisplayMode<T extends { assetType: 'audio' }>(
        asset: T,
    ): AudioDisplayMode;
    getDisplayMode<T extends { assetType: 'video' }>(
        asset: T,
    ): VideoDisplayMode;
    getDisplayMode<T extends { assetType: 'video' | 'audio' }>(
        asset: T,
    ): AudioDisplayMode | VideoDisplayMode {
        const container = this.player.getContainer();
        const video = container.querySelector('video');
        const isVideo = isVideoAsset(asset);

        // TODO: not implemented "Overlay" [v] | "Minimized" [av] | "Sticky" [v]

        if (this.jw?.getFullscreen()) {
            return 'Fullscreen';
        }
        if (isVideo && document.pictureInPictureElement === video) {
            return 'PictureInPicture';
        }
        if (isVideo && this.player.getFloating()) {
            return 'Floating';
        }
        return 'Normal';
    }

    getPlaybackDevice(): PlaybackDevice {
        const container = this.player.getContainer();
        const video = container.querySelector('video');

        if (video?.webkitCurrentPlaybackTargetIsWireless) {
            return 'Airplay' as const;
        }
        if (this.casting) {
            return 'Chromecast' as const;
        }
        return 'Device';
    }

    getPlayerType(
        asset: BrandedAsset,
    ): 'VideoPlayer' | 'AudioPlayer' | 'TtsPlayer' {
        if (isVideoAsset(asset)) {
            return 'VideoPlayer';
        }
        if (this.tts?.enabled) {
            return 'TtsPlayer';
        }
        return 'AudioPlayer';
    }

    async getTracker(): Promise<Tracker | null> {
        if (this.tracker === null) {
            return new Promise((resolve) =>
                this.once('ready', () => {
                    resolve(this.tracker);
                }),
            );
        }

        return this.tracker;
    }

    setup() {
        // tear down any previously created instances/listeners so a
        // repeated setup() call cannot orphan them
        this.adEvents?.destroy();
        this.playbackEvents?.destroy();
        this.stopListening();

        // prevent asset to be changed with playnext before stream completes
        this.playerAsset = this.player.getRawAsset() as BrandedAsset;

        // must be registered BEFORE constructing PlaybackEvents: for a
        // playnext item (session.wasViewed === true), PlaybackEvents'
        // own 'playlistItem' handler synchronously triggers 'Load', which
        // flows into onPlaybackEvent and reads this.playEntry/playTrigger.
        // Listener order on the player's 'playlistItem' event follows
        // registration order, so onNewAsset must run first to reset the
        // sticky override before that synchronous Load is processed.
        this.listenTo(this.player, 'playlistItem', this.onNewAsset);

        this.adEvents = new AdEvents(this.player);
        this.playbackEvents = new PlaybackEvents(this.player, {
            vodInterval: this.intervals.vod,
            liveInterval: this.intervals.live,
        });

        // initialize only once
        // webpack freaks out and sometimes treats it as a module and sometimes as a default export
        getPulseTracker()
            .then((tracker) => {
                this.onReady(tracker);
            })
            .catch(() => {
                this.setupErrorEventEmitted = true;
                this.trigger('setupError');
            });

        if (this.jw) {
            this.listenTo(this.jw, 'cast', this.onCast);
        }

        this.listenTo(this.adEvents, 'all', this.onAdEvent);
        this.listenTo(this.playbackEvents, 'all', this.onPlaybackEvent);
        this.listenTo(this.player, 'viewable', this.onViewable);
    }

    onNewAsset() {
        this.playEntry = undefined;
        this.playTrigger = null;
    }

    onCast(e: { active: boolean }) {
        this.casting = e.active;
    }

    onViewable(data: { viewable: 0 | 1 }) {
        if (!data.viewable) {
            return;
        }

        this.stopListening(this.player, 'viewable', this.onViewable);
        this.trackImpressionPlayer({
            foldPosition: getFoldPosition(this.player.getContainer()),
        });
    }

    onReady(tracker: Tracker) {
        this.tracker = tracker;
        this.trigger('ready');
    }

    protected getQuality() {
        const visualQuality = this.jw?.getVisualQuality();
        if (!visualQuality?.level?.height || !visualQuality?.level?.width) {
            return 'Unknown';
        }

        const { width, height } = visualQuality.level;
        const aspectRatio = width / height;
        const quality = aspectRatio >= 1 ? width : height;
        if (quality >= 1080) {
            return 'High';
        }
        if (quality < 480) {
            return 'Low';
        }
        return 'Medium';
    }

    onPlaybackEvent(
        type: 'Pause',
        data: {
            playMethod: 'interaction' | null;
            duration: number;
            position: number;
        },
    ): void;
    onPlaybackEvent(
        type: VideoEngagementType | AudioEngagementType,
        data: Record<string, unknown> = {},
    ): void {
        if (this.isMockAsset) {
            return;
        }

        logger(this.getName()).log('onPlaybackEvent', type, data);

        const asset = this.playerAsset;
        const container = this.player.getContainer();
        const video = container.querySelector('video');
        const pulseConfig = this.player.config.get('pulse');
        const tags = (this.playerAsset._embedded?.tags ?? []).map(
            ({ tag }) => tag,
        );
        const additional = asset.additional || {};

        // interaction
        const viewtimeMs = data.duration ? Number(data.duration) : 0;
        const positionMs = data.position ? Number(data.position) : 0;

        const prevPositionMs = this.prevPositionMs;
        this.prevPositionMs = positionMs;
        const appState: AppState =
            document.visibilityState === 'hidden' ? 'Background' : 'Foreground';

        const playbackSpeed = this.jw?.getPlaybackRate();
        const playEntry = resolvePlayEntry(
            this.playEntry,
            data.playbackSource as string | undefined,
        );

        // the play trigger describes how this asset's playback flow
        // began; it is (re)computed on asset-start events and cached so
        // later events of the same asset (Watch/Pause/Stop/Seek) report
        // the same value, even though they may lack playMethod/
        // playbackSource
        if (
            ASSET_START_ENGAGEMENT_TYPES.has(type) ||
            this.playTrigger === null
        ) {
            this.playTrigger = this.getPlayTrigger(
                data.playMethod as jwplayer.PlayReason | string | null,
                data.playbackSource as string | undefined,
            );
        }
        const playTrigger = this.playTrigger;

        const interaction = {
            positionMs,
            prevPositionMs,
            viewtimeMs,
            appState,
            playEntry,
            playbackDevice: this.getPlaybackDevice(),
            isMuted: this.player.getMute(),
            playType: this.getPlayType(playTrigger),
            ...(playbackSpeed !== undefined && { playbackSpeed }),
            ...(type === 'Seek' && {
                seekFromMs: data.seekFromMs as number | undefined,
                seekToMs: data.seekToMs as number | undefined,
            }),
        };

        // object
        const durationMs = asset.streamType !== 'live' ? asset.duration : -1;
        const objectId = asset.id.toString();
        const objectSdrn = `sdrn:${asset.provider}:${asset.assetType}:${asset.id}`;
        const playerId = this.player.sessionId;
        const playlistId =
            this.playerAsset.additional?.metadata?.['playlistId'];
        const queuePosition = data.queuePosition as number | undefined;
        const variant = additional.metadata?.['contentType'] ?? 'Unknown';
        const imageUrl = asset.images.main;
        const category = asset.category.title;
        const streamAccessLevelKeys = Object.keys(additional.access || {});
        const streamAccessLevel =
            streamAccessLevelKeys.length > 0
                ? streamAccessLevelKeys
                      .map((accessLevel) => capitalizeFirstLetter(accessLevel))
                      .join(',')
                : 'Free';
        const isLoop = this.jw?.getConfig().repeat ?? false;

        // track
        if (isVideoAsset(asset)) {
            const aspectRatio = normalizeAspectRatio(
                (video?.offsetWidth || 16) / (video?.offsetHeight || 9),
            );
            this.tracker?.trackEngagementVideo({
                engagement: {
                    engagementType: type,
                },
                interaction: {
                    ...interaction,
                    appState,
                    displayMode: this.getDisplayMode(asset),
                    playTrigger,
                    ...(isLoop
                        ? { loopCount: (data.loopCount as number) ?? 1 }
                        : undefined),
                    quality: this.getQuality(),
                },
                object: {
                    durationMs,
                    ...(pulseConfig?.elementSize && {
                        elementSize: pulseConfig.elementSize,
                    }),
                    name: asset.title,
                    objectNamespace: asset.provider,
                    objectId,
                    objectSdrn,
                    playerId,
                    playlistId,
                    queueType: 'Automatic', // currently the only possible option
                    ...(queuePosition && { queuePosition }),
                    streamType: asset.streamType,
                    variant,
                    category,
                    tags,
                    season: asset.series?.seasonNumber?.toString(),
                    episode: asset.series?.episodeNumber?.toString(),
                    imageUrl,
                    playTypeIntent: this.getPlayTypeIntent(),
                    streamAccessLevel,
                    player: this.getPlayerType(asset),
                    aspectRatio,
                    isAd: asset.provider === 'brandstudio',
                    isLoop,
                    isSponsored: additional.metadata?.sponsored === 'true',
                },
            });
        } else if (isAudioAsset(asset)) {
            this.tracker?.trackEngagementAudio({
                engagement: {
                    engagementType: type,
                },
                interaction: {
                    ...interaction,
                    displayMode: this.getDisplayMode(asset),
                    playTrigger,
                },
                object: {
                    durationMs,
                    ...(pulseConfig?.elementSize && {
                        elementSize: pulseConfig.elementSize,
                    }),
                    name: asset.title,
                    objectNamespace: asset.provider,
                    objectId,
                    objectSdrn,
                    playerId,
                    playlistId,
                    queueType: 'Automatic', // currently the only possible option
                    ...(queuePosition && { queuePosition }),
                    streamType: asset.streamType,
                    variant,
                    category,
                    tags,
                    season: asset.series?.seasonNumber?.toString(),
                    episode: asset.series?.episodeNumber?.toString(),
                    imageUrl,
                    playTypeIntent: this.getPlayTypeIntent(),
                    streamAccessLevel,
                    player: this.getPlayerType(asset),
                },
            });
        }
    }

    onAdEvent(type: VideoAdEngagementType, data: AdData) {
        if (
            this.isMockAsset ||
            !['AdStart', 'AdStop', 'AdSkip'].includes(type)
        ) {
            return;
        }

        logger(this.getName()).log('onAdEvent', type, data);

        return this.tracker?.trackEngagementVideoAd({
            engagement: {
                engagementType: type,
            },
            interaction: {
                positionMs: data.positionMs,
                isMuted: this.player.getMute(),
                playType: this.getPlayType(this.playTrigger),
            },
            object: {
                adDurationMs: data.durationMs ?? -1,
                adFormat: data.adFormat ?? 'unknown',
                adId: data.id!,
                adSequencePosition: data.sequence!,
                adSkippable: false,
                adSlot: data.type!,
                mediaAssetId: String(this.playerAsset.id),
                mediaAssetSdrn: `sdrn:${this.playerAsset.provider}:${this.playerAsset.assetType}:${this.playerAsset.id}`,
                mediaAssetVariant:
                    this.playerAsset.additional?.metadata?.contentType ||
                    'Unknown',
                objectSdrn: `sdrn:${this.provider}:videoad:${data.id}`,
                player: 'VideoPlayer',
                playerId: this.player.sessionId,
            },
        });
    }

    async trackEngagementTeaser(data: {
        containerName: string;
        contentSource: string;
        creativeURL?: string;
        elementName: string;
        elementSize?: 'XS' | 'S' | 'M' | 'L' | 'XL';
        foldPosition?: 'Above' | 'Below';
        name: string;
        primaryAxis?: 'Horizontal' | 'Vertical';
        primaryPosition?: number;
        secondaryPosition?: number;
    }): Promise<void> {
        if (this.isMockAsset || !this.tracker) {
            return;
        }
        logger(this.getName()).log('trackEngagementTeaser', data);

        await this.tracker.trackEngagementTeaser({
            engagement: {
                action: 'Click',
                elementName: data.elementName,
            },
            object: {
                ...('creativeURL' in data && data.creativeURL
                    ? { creativeURL: data.creativeURL }
                    : undefined),
                ...('elementText' in data && data.elementText
                    ? { elementText: data.elementText }
                    : undefined),
                contentSource: normalizeContentSource(data.contentSource),
                name: data.name,
                objectId: this.player.sessionId,
                objectSdrn: `sdrn:${this.provider}:player:${this.player.sessionId}:teaser:${normalizeString(data.name)}`,
                title: this.playerAsset.title,
            },
            placement: {
                columnName: 'main',
                containerName: data.containerName,
                foldPosition: data.foldPosition ?? 'Unknown',
                positionInBundle: 1,
                primaryAxis: data.primaryAxis ?? 'Horizontal',
                primaryPosition: data.primaryPosition ?? 1,
                secondaryPosition: data.secondaryPosition ?? 1,
            },
            target: {
                info: {},
            },
        });
    }

    async trackEngagementUIElement(data: {
        elementName: string;
        elementSize?: 'XS' | 'S' | 'M' | 'L' | 'XL';
        elementText?: string;
        elementType: string;
        name: string;
        playEntry?: string;
        targetUrl?: string;
    }): Promise<void> {
        if (this.isMockAsset || !this.tracker) {
            return;
        }
        logger(this.getName()).log('trackEngagementUIElement', data);

        if ('playEntry' in data) {
            this.playEntry = data.playEntry;
        }

        await this.tracker.trackEngagementUIElement({
            engagement: {
                action: 'Click',
                elementName: data.elementName,
            },
            object: {
                ...('elementText' in data && data.elementText
                    ? { elementText: data.elementText }
                    : undefined),
                elementType: data.elementType,
                name: data.name,
                objectId: this.player.sessionId,
                objectSdrn: `sdrn:${this.provider}:player:${this.player.sessionId}:uielement:${normalizeString(data.name)}`,
            },
            placement: {
                containerName: 'player',
            },
            ...(data.targetUrl
                ? { target: { info: { url: data.targetUrl } } }
                : undefined),
        });
    }

    async trackImpressionPlayer(data: {
        foldPosition?: FoldPosition;
        playEntry?: string;
    }): Promise<void> {
        if (this.isMockAsset || !this.tracker) {
            return;
        }
        logger(this.getName()).log('trackImpressionPlayer', data);

        if ('playEntry' in data) {
            this.playEntry = data.playEntry;
        }

        await this.tracker.trackImpressionPlayer({
            placement: {
                foldPosition: data.foldPosition ?? 'Unknown',
            },
            object: {
                mediaAssetId: String(this.playerAsset.id),
                mediaAssetName: this.playerAsset.title,
                mediaAssetSdrn: `sdrn:${this.playerAsset.provider}:${this.playerAsset.assetType}:${this.playerAsset.id}`,
                objectSdrn: `sdrn:${this.provider}:player:${this.player.sessionId}`,
                player: this.getPlayerType(this.playerAsset),
                playerEngine: 'WebPlayer',
                playerId: this.player.sessionId,
            },
        });
    }

    async trackImpressionUIElement(data: {
        elementSize?: 'XS' | 'S' | 'M' | 'L' | 'XL';
        elementText?: string;
        elementType: string;
        foldPosition?: FoldPosition;
        name: string;
        sponsor?: string;
    }): Promise<void> {
        if (this.isMockAsset || !this.tracker) {
            return;
        }
        logger(this.getName()).log('trackImpressionUIElement', data);

        await this.tracker.trackImpressionUIElement({
            object: {
                ...('elementSize' in data && data.elementSize
                    ? { elementSize: data.elementSize }
                    : undefined),
                ...('elementText' in data && data.elementText
                    ? { elementText: data.elementText }
                    : undefined),
                elementType: data.elementType,
                name: data.name,
                objectId: this.player.sessionId,
                objectSdrn: `sdrn:${this.provider}:player:${this.player.sessionId}:uielement:${normalizeString(data.name)}`,
            },
            placement: {
                containerName: 'player',
                foldPosition: data.foldPosition ?? 'Unknown',
            },
        });
    }

    destroy() {
        this.stopListening();

        this.adEvents.destroy();
        this.playbackEvents.destroy();
    }
}
